import { useCallback, useEffect, useRef } from "react";

function clearTimeoutIfExists(timeout: ReturnType<typeof setTimeout> | null) {
    if (timeout !== null) {
        clearTimeout(timeout);
    }
}

/**
 * Returns a debounced version of the provided function.
 *
 * @param func The function to debounce.
 * @param wait The number of milliseconds to delay.
 *
 * @returns A debounced version of the provided function.
 */

export function useDebouncedCallback(func: Function, wait: number) {
    const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(
        () => () => {
            clearTimeoutIfExists(timeout.current);
        },
        []
    );

    return useCallback(
        (...args: unknown[]) => {
            const later = () => {
                clearTimeoutIfExists(timeout.current);
                func(...args);
            };

            clearTimeoutIfExists(timeout.current);
            timeout.current = setTimeout(later, wait);
        },
        [func, wait]
    );
}
